/**
 * CJdropshipping Alternatives 主页面 — 文案常量
 * 文案来源：docs/CJdropshipping vs 竞品专页面/CJ alternatives .html（以 HTML 为准）
 *
 * 结构：
 *   META       — SEO 标题/描述（与 fetch.js 保持一致）
 *   HERO       — Hero Banner 文案
 *   SELLPOINTS — 服务卖点标签数组
 *   COMPARE    — 对比卡片区域
 *   FIT        — Who Should Choose 区域
 *   ADVANTAGES — 核心优势 8 张卡片
 *   TESTIMONIALS — 用户评价跑马灯
 *   FAQ        — 主页专属 FAQ（左图 + info-grid + accordion，与子页 FAQ 不同）
 *   BOTTOM_CTA — 底部 CTA
 */
/**
 * 【纠正】图片改用 CDN 路径（resources/egg/alternatives/），不再使用外链 Unsplash URL。
 * 参照 odm/index.jsx 模式，函数接受 cdnHost 参数，所有图片路径拼接为 `${cdnHost}/resources/egg/alternatives/filename`。
 * 调用方（fetch.js / server render）需传入 cdnHost。
 */
export function getAlternativesConstants(cdnHost) {
  const img = (name) => `${cdnHost || ''}/resources/egg/alternatives/${name}`;
  return {
    META: {
      title: 'Find the Best CJdropshipping Alternative for Your Business',
      description:
        'Compare CJdropshipping with other ecommerce and dropshipping platforms for sourcing, fulfillment, shipping, branding, and long-term growth.',
    },

    HERO: {
      kicker: 'Compare CJdropshipping alternatives',
      title: 'Find the Best <strong>CJdropshipping Alternative</strong> for Your Business',
      desc: 'Compare CJdropshipping with leading ecommerce and dropshipping platforms to discover which solution gives you the best fit for sourcing, fulfillment, shipping, branding, and long-term growth.',
      ctaText: 'GET STARTED',
      ctaUrl: '/home',
      heroNote: 'Compare faster. Choose smarter.',
      heroImage: img('hero_banner.jpg'),
      heroImageAlt: 'Ecommerce fulfillment dashboard',
      statCardLabel: '360°',
      statCardDesc: 'Platform comparison',
      floatCardTitle: 'Best-fit score',
      floatCardDesc: 'Compare sourcing, delivery, branding, and growth potential at a glance.',
    },

    SELLPOINTS: [
      'Product Sourcing',
      'Global Fulfillment',
      'Quality Inspection',
      'Custom Packaging',
      'Print on Demand',
      'Global Warehouses',
      'Dedicated Agent Support',
    ],

    COMPARE: {
      kicker: 'Platform Comparison',
      title: 'Compare <strong>CJdropshipping</strong> with Other Dropshipping Platforms',
      desc: 'Find the right platform for your ecommerce growth.',
      cards: [
        {
          title: 'CJdropshipping vs Zendrop',
          href: '/cjdropshipping-vs-zendrop',
          logo1: 'CJdropshipping',
          logo2: 'Zendrop',
          logo2Color: '#111827',
          featured: true,
        },
        {
          title: 'CJdropshipping vs AutoDS',
          href: '/cjdropshipping-vs-autods',
          logo1: 'CJdropshipping',
          logo2: 'AutoDS',
          logo2Color: '#2563eb',
        },
        {
          title: 'CJdropshipping vs Spocket',
          href: '/cjdropshipping-vs-spocket',
          logo1: 'CJdropshipping',
          logo2: 'Spocket',
          logo2Color: '#7c3aed',
        },
        {
          title: 'CJdropshipping vs AliExpress',
          href: '/cjdropshipping-vs-aliexpress',
          logo1: 'CJdropshipping',
          logo2: 'AliExpress',
          logo2Color: '#e43225',
        },
        {
          title: 'CJdropshipping vs Day One Fulfillment',
          href: '/cjdropshipping-vs-day-one-fulfillment',
          logo1: 'CJdropshipping',
          logo2: 'Day One',
          logo2Color: '#ff7700',
        },
      ],
      readMoreText: 'Read comparison →',
      badgeText: 'Compare',
    },

    FIT: {
      kicker: 'Seller Fit Guide',
      title: 'Who Should Choose <strong>CJdropshipping?</strong>',
      desc: 'CJdropshipping is a better fit for sellers who:',
      imageUrl: img('fit_who_should_choose.jpg'),
      badgeText: '✓ Best Fit for Growth Sellers',
      floatTitle: 'Brand-building ecommerce sellers',
      floatDesc: 'A stronger fit for merchants who want sourcing, fulfillment, branding, and long-term operational support.',
      items: [
        { text: 'Need reliable product sourcing' },
        { text: 'Want fulfillment support for dropshipping orders' },
        { text: 'Sell through Shopify, WooCommerce, TikTok Shop, or eBay' },
        { text: 'Want to test or sell POD products' },
        { text: 'Need custom packaging or branding services' },
        { text: 'Want to improve shipping and customer experience' },
        { text: 'Plan to move from marketplace reselling to brand-based ecommerce' },
        { text: 'See dropshipping as a long-term business, not just short-term product testing' },
      ],
      summaryTitle: 'Why CJdropshipping stands out',
      summaryDesc: 'CJdropshipping is ideal for sellers who want to go beyond simple product copying. It helps you build a stronger business foundation through sourcing, fulfillment, customization, and branding support.',
      footerCtaText: 'CJdropshipping stands out because it gives sellers the operational support needed to source better, fulfill faster, brand professionally, and scale with more confidence.',
      footerCtaBtn: 'GET STARTED',
      footerCtaUrl: '/home',
    },

    ADVANTAGES: {
      kicker: 'Competitive Advantages',
      title: 'How <strong>CJdropshipping</strong> beat the competition',
      desc: 'CJdropshipping is a full dropshipping supply chain partner. It helps sellers go beyond product listing with sourcing, fulfillment, shipping, branding, and dedicated support.',
      cards: [
        {
          num: '01',
          title: 'Full Dropshipping Supply Chain Support',
          desc: 'CJdropshipping covers more than listing. It supports product sourcing, procurement, warehousing, fulfillment, shipping, and after-sales workflows.',
          imageUrl: img('adv_supply_chain.jpg'),
        },
        {
          num: '02',
          title: 'Reliable Product Sourcing',
          desc: 'Sellers can submit sourcing requests and find products through CJdropshipping\'s supplier resources, making it easier to discover better products and improve margins.',
          imageUrl: img('adv_sourcing.jpg'),
        },
        {
          num: '03',
          title: 'Quality Inspection Before Shipping',
          desc: 'CJdropshipping helps inspect products and packages before shipment, reducing the risk of defects, complaints, refunds, and negative reviews.',
          imageUrl: img('adv_quality_inspection.jpg'),
        },
        {
          num: '04',
          title: 'Global Warehousing',
          desc: 'With warehouse support in different markets, CJdropshipping helps sellers store inventory closer to customers and improve fulfillment efficiency.',
          imageUrl: img('adv_warehousing.jpg'),
        },
        {
          num: '05',
          title: 'End-to-End Order Fulfillment',
          desc: 'CJdropshipping can handle order processing, picking, packing, shipping, tracking, and delivery support, helping sellers reduce manual work.',
          imageUrl: img('adv_fulfillment.jpg'),
        },
        {
          num: '06',
          title: 'Global Shipping Solutions',
          desc: 'CJdropshipping provides global logistics options to help sellers deliver products to customers more efficiently and reliably.',
          imageUrl: img('adv_shipping.jpg'),
        },
        {
          num: '07',
          title: 'Custom Packaging and Branding',
          desc: 'Sellers can create branded packaging, inserts, labels, and unboxing experiences, making it easier to build a real ecommerce brand.',
          imageUrl: img('adv_custom_packaging.jpg'),
        },
        {
          num: '08',
          title: 'Dedicated Agent Support',
          desc: 'CJdropshipping provides service support to help sellers with sourcing, fulfillment, logistics, and operational issues as their business grows.',
          imageUrl: img('adv_dedicated_support.jpg'),
        },
      ],
      footerCta: {
        text: 'CJdropshipping stands out because it gives sellers the operational support needed to source better, fulfill faster, brand professionally, and scale with more confidence.',
        ctaText: 'GET STARTED',
        ctaUrl: '/home',
      },
    },

    TESTIMONIALS: {
      kicker: 'Real Seller Stories',
      title: 'Why Growing Sellers <strong>Stay with CJdropshipping</strong>',
      desc: 'Hear how ecommerce sellers use CJdropshipping to streamline sourcing, fulfillment, branding, and shipping while building stronger long-term businesses.',
      // rows[0] → 左滚行（marqueeLeft），rows[1] → 右滚行（marqueeRight）
      // SharedComponents.jsx 内部会各自 concat 一次用于无缝循环
      rows: [
        [
          {
            name: 'Emily Carter',
            role: 'Shopify Store Owner',
            country: 'United States',
            avatarUrl: img('32.jpeg'),
            quote: '"CJdropshipping helped me simplify my fulfillment process. Instead of manually coordinating with multiple suppliers, I can manage sourcing, orders, and shipping in one place. It saves me a lot of time every week."',
            date: 'Verified Seller Insight',
          },
          {
            name: 'Daniel Hughes',
            role: 'Ecommerce Entrepreneur',
            country: 'United Kingdom',
            avatarUrl: img('12.jpeg'),
            quote: '"As my store started growing, I needed more than just a product listing tool. CJdropshipping gave me access to sourcing, fulfillment, and packaging support, which made it easier to scale my business."',
            date: 'Verified Seller Insight',
          },
          {
            name: 'Sofia Martinez',
            role: 'Dropshipping Seller',
            country: 'Spain',
            avatarUrl: img('47.jpeg'),
            quote: '"One of the biggest advantages for me is product sourcing. When I cannot find the right product, CJdropshipping helps me look for alternatives and connect with supply options that fit my store."',
            date: 'Verified Seller Insight',
          },
        ],
        [
          {
            name: 'Liam Walker',
            role: 'DTC Brand Founder',
            country: 'Australia',
            avatarUrl: img('15.jpeg'),
            quote: '"I wanted my store to feel more professional, and CJdropshipping\'s custom packaging and branding options helped a lot. It made my customer experience feel less generic."',
            date: 'Verified Seller Insight',
          },
          {
            name: 'Chloe Anderson',
            role: 'Online Store Operator',
            country: 'Canada',
            avatarUrl: img('5.jpeg'),
            quote: '"Managing products, orders, and fulfillment through one platform makes daily operations much easier. CJdropshipping is especially helpful if you want to reduce repetitive manual work."',
            date: 'Verified Seller Insight',
          },
          {
            name: 'Marco Rossi',
            role: 'Ecommerce Seller',
            country: 'Italy',
            avatarUrl: img('60.jpeg'),
            quote: '"CJdropshipping feels like a better fit for sellers who want to build a serious dropshipping business. It is not only about listing products, but also about improving sourcing, shipping, and the overall customer experience."',
            date: 'Verified Seller Insight',
          },
        ],
      ],
    },

    /**
     * 主页专属 FAQ — 左图 + info-grid(2x2) + 右侧 accordion 布局
     * 与子页面 VsFaqSection（左侧摘要卡片 + accordion）布局完全不同，各自独立实现。
     */
    FAQ: {
      kicker: 'FAQ',
      title: 'Frequently Asked Questions About <strong>CJdropshipping Alternatives</strong>',
      desc: 'Compare CJdropshipping with other dropshipping platforms across sourcing, fulfillment, automation, warehousing, POD, branding, and long-term ecommerce growth.',
      imageUrl: img('faq_comparison.jpg'),
      imageAlt: 'CJdropshipping alternatives FAQ comparison',
      overlayTitle: 'Which platform fits your growth plan?',
      overlayDesc: 'Use these answers to compare CJdropshipping with other dropshipping tools and choose the right solution for sourcing, fulfillment, branding, and scale.',
      infoCards: [
        {
          title: 'All-in-One Support',
          desc: 'CJdropshipping combines sourcing, fulfillment, warehousing, POD, packaging, and agent support in one platform.',
        },
        {
          title: 'Built for Growth',
          desc: 'Compare alternatives based on what matters most: operations, delivery experience, branding, and long-term scalability.',
        },
      ],
      items: [
        {
          q: 'Why should I compare CJdropshipping with other dropshipping platforms?',
          a: 'Comparing CJdropshipping with other platforms helps you understand which solution best fits your business model. Some tools focus on product research, some focus on automation, some focus on supplier access, and others focus on fulfillment. CJdropshipping stands out by combining sourcing, fulfillment, warehousing, POD, custom packaging, and branding support in one platform.',
        },
        {
          q: 'What makes CJdropshipping different from other dropshipping tools?',
          a: 'CJdropshipping is more than a listing tool or supplier marketplace. It supports product sourcing, order fulfillment, global warehouses, quality inspection, POD, custom packaging, 3PL fulfillment, and dedicated agent support, making it more suitable for sellers who want to build a scalable dropshipping business.',
        },
        {
          q: 'Is CJdropshipping better than AliExpress for dropshipping?',
          a: 'CJdropshipping is usually better for sellers who need more control over sourcing, fulfillment, shipping, packaging, and customer experience. AliExpress can be useful for product research and low-cost product discovery, but it is not built as a dedicated dropshipping fulfillment platform.',
        },
        {
          q: 'Is CJdropshipping better than AutoDS?',
          a: 'CJdropshipping and AutoDS serve different needs. AutoDS focuses more on dropshipping automation, product importing, and price or stock monitoring. CJdropshipping is better for sellers who need product sourcing, fulfillment, warehousing, POD, custom packaging, and brand-building support.',
        },
        {
          q: 'Is CJdropshipping better than Spocket?',
          a: 'CJdropshipping is a better fit for sellers who want flexible product sourcing, global fulfillment, custom packaging, POD, 3PL fulfillment, and branding support. Spocket is more suitable for sellers who mainly want to browse and import products from supplier catalogs, especially US and EU suppliers.',
        },
        {
          q: 'Is CJdropshipping good for beginners?',
          a: 'Yes. CJdropshipping is beginner-friendly because it helps sellers start with product sourcing, product import, order fulfillment, and shipping support without needing to manage every supplier and fulfillment step manually.',
        },
        {
          q: 'Is CJdropshipping good for scaling sellers?',
          a: 'Yes. CJdropshipping is especially useful for scaling sellers because it supports global warehouses, 3PL fulfillment, custom packaging, POD, dedicated agent support, and branding services. These features help sellers move from product testing to long-term ecommerce growth.',
        },
        {
          q: 'Why choose CJdropshipping over other alternatives?',
          a: 'Choose CJdropshipping if you want more than a single-purpose tool. It brings together sourcing, fulfillment, quality inspection, warehousing, global shipping, custom packaging, POD, and agent support, helping sellers build a more complete and scalable dropshipping business.',
        },
      ],
      footerCtaText: 'Need one platform for sourcing, fulfillment, shipping, packaging, POD, and brand growth?',
      footerCtaBtn: 'GET STARTED',
      footerCtaUrl: '/home',
    },

    BOTTOM_CTA: {
      kicker: 'Ready to Scale Smarter?',
      title: 'Need More Than a <strong>Crosslisting Tool?</strong><br/>Try CJdropshipping',
      desc: 'CJdropshipping helps you source, fulfill, ship, and brand your dropshipping products — all in one platform.',
      ctaText: 'Explore CJ Service',
      ctaUrl: '/home',
      bgImage: img('hero_banner.jpg'),
    },
  };
}
